/** Deleting a conversation, from the conversation page or from its row on a list.
 *
 * One control for both entry points, so they share the rule (only the person who
 * started a conversation, and never a scheduled pass), the two-click confirm and the
 * call. The server hides the run rather than erasing it (``set-hidden``). */
import { useState } from "react";

import { useSetStatefulRunHidden } from "../api/hooks/useStatefulAgents";
import styles from "./DeleteConversation.module.css";

interface DeletableRun {
  run_id: string;
  kind: string;
  is_owner?: boolean;
  hidden_at?: string | null;
}

const NOT_OWNER = "Only the person who started this conversation can delete it";

const TrashIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

export const DeleteConversation = ({
  run,
  trigger,
  notOwner,
  className,
  dangerClassName,
  disabled = false,
  onDeleted,
  onError,
}: {
  run: DeletableRun;
  /** The page offers "Delete conversation"; a list row offers a trash icon. */
  trigger: "text" | "icon";
  /** The page leaves the control out for someone else's conversation; a list shows
   * it disabled, so every row has the same shape. */
  notOwner: "absent" | "disabled";
  className: string;
  dangerClassName: string;
  /** Another action on the same run is in flight. */
  disabled?: boolean;
  onDeleted: () => void;
  /** Where a failure is shown; without it the control shows it beside itself. */
  onError?: (message: string | null) => void;
}) => {
  const setHidden = useSetStatefulRunHidden(run.run_id);
  // Two clicks, not a browser confirm.
  const [confirming, setConfirming] = useState(false);
  const [ownError, setOwnError] = useState<string | null>(null);
  const reportError = onError ?? setOwnError;

  if (run.kind === "scheduled" || run.hidden_at) return null;
  const allowed = !!run.is_owner;
  if (!allowed && notOwner === "absent") return null;
  const busy = disabled || setHidden.isLoading;

  const remove = async () => {
    reportError(null);
    try {
      await setHidden.mutateAsync({ hidden: true });
      setConfirming(false);
      onDeleted();
    } catch (e) {
      setConfirming(false);
      reportError(e instanceof Error ? e.message : "Could not delete this conversation.");
    }
  };

  return (
    // A list row opens on click; nothing pressed here may open it.
    <span className={styles.control} onClick={(event) => event.stopPropagation()}>
      {confirming ? (
        <>
          <button
            type="button"
            className={`${className} ${dangerClassName}`}
            disabled={busy}
            onClick={() => void remove()}
          >
            {setHidden.isLoading ? "Deleting…" : "Delete"}
          </button>
          <button
            type="button"
            className={className}
            disabled={busy}
            onClick={() => setConfirming(false)}
          >
            Cancel
          </button>
        </>
      ) : (
        // The tooltip sits on a wrapper: a disabled button gets no hover events.
        <span title={allowed ? undefined : NOT_OWNER}>
          <button
            type="button"
            className={className}
            disabled={busy || !allowed}
            title={allowed && trigger === "icon" ? "Delete" : undefined}
            aria-label={trigger === "icon" ? "Delete conversation" : undefined}
            onClick={() => setConfirming(true)}
          >
            {trigger === "icon" ? <TrashIcon /> : "Delete conversation"}
          </button>
        </span>
      )}
      {!onError && ownError && <span className={styles.error}>{ownError}</span>}
    </span>
  );
};
